import { notification } from "antd";
import cuid from "cuid";
import {
  filter,
  find,
  get,
  head,
  isArray,
  isEmpty,
  isNil,
  isObject,
  last,
  sortBy,
} from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import format from "string-template";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import {
  DUPLICATE_PROJECT,
  UPDATE_PROJECT,
} from "utils/api/graphql/mutations/projects";
import { SEND_B2C_QUOTE } from "utils/api/graphql/mutations/quotes";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { OFFER } from "utils/api/graphql/queries/offers";
import { PROJECT } from "utils/api/graphql/queries/projects";
import {
  ACTION_TYPES,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  INSURANCE_PLAN_PARAMS_RULES,
  KEYS,
  PROCESS_TYPES,
  RELATIONSHIP,
  SCREENS,
  TEMPLATES,
  TEMPLATE_TYPES,
} from "utils/constants";
import { redirectAfterDuplicateProject } from "utils/helpers/project";
import { getMinDeltaStart } from "utils/helpers/array";
import { normalizeAcorisProfile } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/insurancePlan";
import { buildCombinationAdditionalInfo } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/combinationCode";
import { getDepartmentFromZipCode } from "utils/helpers/address";
import { generateSQLCondition } from "utils/helpers/graphql";
import { filterByConditions } from "../../../..";
import {
  checkProductUpsellsAndPacks,
  hasOptionalCoverage,
} from "../utils/offerUpsells";
import { sendB2CQuote } from "../utils/sendB2CQuote";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import NotEligible from "../NotEligible";
import View from "./View";

const findStatus = (data, template) => {
  return find(data, ({ template: _ }) => _ === template);
};

const Products = ({
  processId,
  tracer,
  project,
  onNext,
  onBack,
  currentStatus,
  setDisplayProject,
  territorial = false,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(false);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [sendQuoteMutation] = useMutation(SEND_B2C_QUOTE);
  const [addLink] = useMutation(CREATE_LINK);

  const [generalCosts, { data }] = useLazyQuery(GENERAL_COSTS);
  const [getOffer] = useLazyQuery(OFFER);
  const [getDeltaStart] = useLazyQuery(INSURANCE_PLANS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        isStandalone: true,
      },
    },
  });
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = tErrors("API", { returnObjects: true });
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);
  const [duplicateProject] = useMutation(DUPLICATE_PROJECT);

  const duplicateProjectOnLocked = () => {
    const { insurancePlans, currentSubscription, nextQuoteStatus, ...fields } =
      project?.fields;

    const Template =
      TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.B2C_ACORIS].PRODUCTS;
    const status = findStatus(
      get(tracer, "flow.process.processStatuses", []),
      Template,
    );
    const actionsToExecute = filterByConditions(
      get(status, "actions", []),
      project,
    );
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({
        id: cuid(),
        status: { id: status },
      }));
    duplicateProject({
      variables: {
        where: { id: project.id },
        data: {
          statuses: [{ id: cuid(), status: { id: status.id } }, ...syncActions],
          fields,
        },
      },
      onCompleted: ({ duplicateProject }) => {
        redirectAfterDuplicateProject({
          duplicateProject,
          tracer,
          statusId: get(status, "id"),
        });
      },
    });
  };
  const [getProject] = useLazyQuery(PROJECT, {
    fetchPolicy: "no-cache",
    variables: { where: { id: project.id } },
  });

  const getSubscriptionForPlan = (projectData, insurancePlanId) =>
    get(projectData, "subscriptions", []).find(
      ({ insurancePlan }) => insurancePlan.id === insurancePlanId,
    );

  const getPossibleStatus = (projectData) => {
    const status = get(
      last(
        sortBy(
          filter(
            projectData.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    return head(
      filterByConditions(sortBy(possibleStatuses, "order"), {
        ...projectData,
        status,
      }),
    );
  };

  const onSendQuote = async (insurancePlanId) => {
    const {
      data: { project },
    } = await getProject();
    const initialInsurancePlans = get(project, "fields.insurancePlans", []);
    const { hasUpsells, hasPacks } = await checkProductUpsellsAndPacks(
      getOffer,
      insurancePlanId,
    );

    const coverageFlags = { hasUpsells, hasPacks };
    const hasOptionalCoverageStep = hasOptionalCoverage(coverageFlags);
    const existingSubscription = getSubscriptionForPlan(
      project,
      insurancePlanId,
    );

    if (initialInsurancePlans.includes(insurancePlanId)) {
      if (existingSubscription?.id) {
        localStorage.setItem(KEYS.SUBSCRIPTION_ID, existingSubscription.id);
      }

      const reselectPayload = {
        fields: {
          ...project.fields,
          ...(hasOptionalCoverageStep
            ? {
                lastSelected: [insurancePlanId],
                hasUpsells,
                hasPacks,
              }
            : {
                hasUpsells: false,
                hasPacks: false,
              }),
          currentSubscription: existingSubscription?.id,
        },
      };

      if (hasOptionalCoverageStep) {
        return onNext({ payload: reselectPayload });
      }

      return onNext({
        payload: reselectPayload,
        onCompleted: () => {
          if (!existingSubscription?.id) {
            return;
          }

          sendB2CQuote({
            project,
            processId,
            insurancePlanId,
            subscriptionId: existingSubscription.id,
            insurancePlan: existingSubscription.insurancePlan,
            possibleStatus: getPossibleStatus(project),
            addLink,
            generateQuoteDocuments,
            sendQuoteMutation,
            onError: () => {
              notification.error({
                message: API_ERRORS.GENERIC,
              });
            },
          });
        },
      });
    }

    const insurancePlans = [
      ...initialInsurancePlans.filter((id) => id !== insurancePlanId),
      insurancePlanId,
    ];

    if (hasOptionalCoverageStep) {
      return onNext({
        payload: {
          fields: {
            ...project.fields,
            selectedToCompare: insurancePlans,
            lastSelected: [insurancePlanId],
            hasUpsells,
            hasPacks,
          },
        },
      });
    }

    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );

    const socleInsurancePlan = get(
      find(products, { insurancePlan: { id: insurancePlanId } }),
      "insurancePlan",
      { id: insurancePlanId },
    );

    addSubscription({
      variables: {
        data: {
          id: cuid(),
          project: { id: project.id },
          insurancePlan: {
            id: insurancePlanId,
          },
          additionalInfo: buildCombinationAdditionalInfo({
            socleInsurancePlan,
            project,
            socleInsurancePlanId: insurancePlanId,
          }),
        },
      },
      onCompleted: ({
        addSubscription: { id: subscriptionId, insurancePlan },
      }) => {
        onNext({
          payload: {
            locked: false,
            fields: {
              ...project.fields,
              insurancePlans: [
                ...get(project, "fields.insurancePlans", []),
                insurancePlanId,
              ],
              selectedToCompare: [
                ...get(project, "fields.selectedToCompare", []),
                insurancePlanId,
              ],
              hasUpsells: false,
              hasPacks: false,
              ...(!isNil(possibleStatus?.nextStatus) && {
                nextQuoteStatus: possibleStatus.nextStatus,
              }),
              currentSubscription: subscriptionId,
            },
          },
          onCompleted: () => {
            sendB2CQuote({
              project,
              processId,
              insurancePlanId,
              subscriptionId,
              insurancePlan,
              possibleStatus,
              addLink,
              generateQuoteDocuments,
              sendQuoteMutation,
              onError: () => {
                notification.error({
                  message: API_ERRORS.GENERIC,
                });
              },
            });
          },
        });
      },
    });
  };

  const onQuoteSubmit = async (insurancePlanId) => {
    const {
      data: { project },
    } = await getProject();

    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );
    setQuoteLoading(true);
    const socleInsurancePlan = get(
      find(products, { insurancePlan: { id: insurancePlanId } }),
      "insurancePlan",
      { id: insurancePlanId },
    );
    addSubscription({
      variables: {
        data: {
          id: cuid(),
          project: { id: project.id },
          insurancePlan: {
            id: insurancePlanId,
          },
          additionalInfo: buildCombinationAdditionalInfo({
            socleInsurancePlan,
            project,
            socleInsurancePlanId: insurancePlanId,
          }),
        },
      },
      onCompleted: ({
        addSubscription: { id: subscriptionId, insurancePlan },
      }) => {
        const source = localStorage.getItem(KEYS.SOURCE);
        addLink({
          variables: {
            data: {
              url: get(
                insurancePlan,
                "config.generatedUrl",
                `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
              ),
              project: { id: project.id },
              subscription: { id: subscriptionId },
              fields: {
                process: { id: processId },
                status: { id: get(possibleStatus, "nextStatus") },
              },
            },
          },
          onCompleted: ({ addLink: linkData }) => {
            if (project.visible || !get(project, "fields.shouldBeHidden"))
              generateQuoteDocuments({
                variables: {
                  data: {
                    project: { id: project.id },
                    insurancePlans: [{ id: insurancePlanId }],
                  },
                },
                onCompleted: () => {
                  sendQuoteMutation({
                    variables: {
                      data: {
                        link: linkData?.id,
                        project: { id: project.id },
                        insurancePlans: [{ id: insurancePlanId }],
                      },
                    },
                    onCompleted: () => {
                      setQuoteLoading(false);
                      notification.open({
                        message: "Devis envoyé",
                        duration: 5,
                        icon: (
                          <IoCheckmarkCircleOutline color={COLORS.C_SUCCESS} />
                        ),
                      });
                    },
                    onError: () => {
                      setQuoteLoading(false);
                      notification.error({
                        message: API_ERRORS.GENERIC,
                      });
                    },
                  });
                },
                onError: () => {
                  setQuoteLoading(false);
                  notification.error({
                    message: API_ERRORS.GENERIC,
                  });
                },
              });
          },
          onError: () => {
            setQuoteLoading(false);
            notification.error({
              message: API_ERRORS.GENERIC,
            });
          },
        });
      },
    });
  };

  const stopProductsLoading = () => {
    setLoading(false);
    setRefetchLoading(false);
  };

  useEffect(() => {
    getDeltaStart({
      onError: stopProductsLoading,
      onCompleted: (result) => {
        const insurancePlans = get(result, "insurancePlans.data", []);
        const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
        const deltaStart = getMinDeltaStart({
          insurancePlans,
          minValue: 1,
          key: INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
        });
        const deltaS = project.ria ? deltaRia : deltaStart;
        const minimumIssuance = moment().add(deltaS, "days");

        const continueWithIssuanceDate = (issuanceDate) => {
          const issuanceDateDifference = moment(issuanceDate)
            .startOf("day")
            .diff(moment().startOf("day"), "days");

          const adherentAge = moment(
            get(project, "contact.user.birthDate"),
          ).startOf("year");

          const age = moment().diff(adherentAge, "years");

          const [startKey, endKey] = project.ria
            ? [
                INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
                INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
              ]
            : [
                INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
                INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
              ];

          const profile = normalizeAcorisProfile(
            get(project, "contact.fields.profile"),
          );
          const zipCode = String(
            get(
              get(project, "contact.telecoms", []).find(
                ({ system }) => system === CONTACT_POINT_SYSTEM.ZIP_CODE,
              ),
              "value",
              "",
            ) ?? "",
          );

          const department = getDepartmentFromZipCode(zipCode) || "";

          const municipality = String(
            get(
              get(project, "contact.telecoms", []).find(
                ({ system }) => system === CONTACT_POINT_SYSTEM.CITY,
              ),
              "value",
              "",
            ) ?? "",
          ).replace(/'/g, "''");

          const socialRegime = get(project, "contact.socialRegime", "");

          const sqlConditions = [
            generateSQLCondition(
              startKey,
              "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
              issuanceDateDifference,
            ),
            generateSQLCondition(
              endKey,
              `r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value ${
                moment(issuanceDate).year() === moment().year()
                  ? "OR CAST(r.value AS NUMERIC) = 0"
                  : ""
              }`,
              issuanceDateDifference,
            ),
            generateSQLCondition(
              INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
              "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
              age,
            ),
            generateSQLCondition(
              INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
              "r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value",
              age,
            ),
            generateSQLCondition(
              INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
              `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
              `'${processId}'`,
            ),
            profile &&
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.PROFILES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${profile}'`,
              ),
            generateSQLCondition(
              INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS,
              `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
              `'${department}'`,
            ),
            generateSQLCondition(
              INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES,
              `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
              `'${zipCode}'`,
            ),
            generateSQLCondition(
              INSURANCE_PLAN_PARAMS_RULES.MUNICIPALITIES,
              `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
              `'${municipality}'`,
            ),
            generateSQLCondition(
              INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES,
              `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
              `'${socialRegime}'`,
            ),
          ].filter(Boolean);

          const insurancePlanFilter = {
            where: {
              isStandalone: true,
            },
            rules: sqlConditions.filter(Boolean),
            custom: {
              AND: [
                {
                  OR: [
                    { startDate: null },
                    {
                      startDate: issuanceDate,
                      operation: "<=",
                    },
                  ],
                },
                {
                  OR: [
                    { endDate: null },
                    {
                      endDate: issuanceDate,
                      operation: ">=",
                    },
                  ],
                },
              ],
            },
          };
          buildGeneralCosts({
            variables: {
              data: {
                project: { id: project.id },
                insurancePlanFilter,
              },
            },
            onError: stopProductsLoading,
            onCompleted: ({ buildGeneralCosts: builtCosts }) => {
              const ids = (Array.isArray(builtCosts) ? builtCosts : [])
                .filter(({ cost }) => cost)
                .map(({ id }) => id);
              if (!ids.length) {
                setProducts([]);
                stopProductsLoading();
                return;
              }
              generalCosts({
                fetchPolicy: "no-cache",
                variables: {
                  isIn: { id: ids },
                },
                onError: stopProductsLoading,
                onCompleted: ({ generalCosts: costsResult }) => {
                  const productsData = omitDeep(
                    get(costsResult, "data", []),
                    "__typename",
                  );

                  const [recommendedProduct] = productsData.splice(0, 1);
                  if (recommendedProduct)
                    productsData.splice(1, 0, recommendedProduct);
                  setProducts(productsData);
                  stopProductsLoading();
                },
              });
            },
          });
        };

        if (project?.expired) {
          notification.open({
            message: format(TRANSLATION.EXPIRATION.TITLE, {
              issuanceDate: minimumIssuance.format(DATE_FORMAT),
            }),
            description: TRANSLATION.EXPIRATION.DESCRIPTION,
            duration: 5,
            icon: <HiOutlineExclamationCircle size={32} />,
            type: "error",
          });

          updateProject({
            variables: {
              where: { id: project.id },
              data: {
                expired: false,
                contract: {
                  ...project.contract,
                  issuanceDate: minimumIssuance,
                },
              },
            },
            onCompleted: ({ updateProject }) => {
              const updatedProject = omitDeep(updateProject, "__typename");
              setDisplayProject(updatedProject);
              continueWithIssuanceDate(minimumIssuance);
            },
          });
        } else {
          const existingIssuance = get(project, "contract.issuanceDate");
          continueWithIssuanceDate(
            existingIssuance ? moment(existingIssuance) : minimumIssuance,
          );
        }
      },
    });
  }, [refetch]);

  const refetchCosts = ({ contact }) => {
    setRefetchLoading(true);
    const relatedPersons = get(contact, "relatedPersons", []).filter((_) => _);
    const hasChildren = !!relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    ).length;
    const hasSpouse = !isEmpty(
      relatedPersons.find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
    );
    const effectiveDate = get(project, "contract.issuanceDate");
    const children = relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    );
    const hasChildOver20 = children.some((child) => {
      const birthDate = isObject(get(child, "identity.user.birthDate"))
        ? get(child, "identity.user.birthDate").toISOString()
        : get(child, "identity.user.birthDate");
      if (!birthDate) return false;
      const age = moment(effectiveDate).diff(birthDate, "years");
      return age >= 20;
    });
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contact,
          fields: {
            ...get(project, "fields"),
            hasRelatedPersons: {
              hasChildren,
              hasSpouse,
              hasChildOver20,
            },
          },
        },
      },
      onCompleted: () => setRefetch((prev) => !prev),
    });
  };
  if (loading || !isArray(products)) return <Loader />;
  return (
    <>
      <Navbar
        project={project}
        currentStatus={currentStatus}
        tracer={tracer}
        showProfile
      />
      {products.length === 0 ? (
        <NotEligible
          project={project}
          onNext={onNext}
          onBack={onBack}
          currentStatus={currentStatus}
          tracer={tracer}
        />
      ) : (
        <View
          products={products}
          count={get(data, "generalCosts.count")}
          onBack={onBack}
          onSubmit={onSendQuote}
          disabledProducts={get(project, "fields.insurancePlans", [])}
          contact={get(project, "contact", {})}
          refetchCosts={refetchCosts}
          downloadSingleDocument={downloadSingleDocument}
          locked={project.locked}
          duplicateProject={duplicateProjectOnLocked}
          refetchLoading={refetchLoading}
          quoteLoading={quoteLoading}
          onQuoteSubmit={onQuoteSubmit}
          territorial={territorial}
        />
      )}
    </>
  );
};

export default Products;
